package diana.soleil.design.iraniancanadienbazzar.core.designsystem.component

import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.detectTapGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.composed
import androidx.compose.foundation.relocation.BringIntoViewRequester
import androidx.compose.foundation.relocation.bringIntoViewRequester
import androidx.compose.ui.geometry.CornerRadius
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Rect
import androidx.compose.ui.geometry.RoundRect
import androidx.compose.ui.graphics.Path
import androidx.compose.ui.graphics.PathOperation
import androidx.compose.ui.graphics.drawscope.Stroke
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.boundsInRoot
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import diana.soleil.design.iraniancanadienbazzar.core.designsystem.theme.AppTheme
import kotlin.math.roundToInt

/**
 * State for the spotlight feature tour (coach marks): registered anchor bounds + the running
 * step index. Create with [rememberTourState]; mark targets with [tourAnchor]; render
 * [FeatureTourOverlay] as the LAST child of the layout root that contains every anchor.
 */
class TourState {
    internal val anchors = mutableStateMapOf<String, Rect>()
    internal var hostOrigin by mutableStateOf(Offset.Zero)

    /** Whether the overlay is currently showing. */
    var visible by mutableStateOf(false)
        internal set

    internal var stepIndex by mutableIntStateOf(0)

    /** Key of the step on screen right now (null when hidden) — drives [tourAnchorInView] scrolling. */
    var currentKey by mutableStateOf<String?>(null)
        internal set

    /** Starts (or restarts) the tour from the first step. */
    fun start() {
        stepIndex = 0
        visible = true
    }

    internal fun dismiss() {
        visible = false
    }
}

@Composable
fun rememberTourState(): TourState = remember { TourState() }

/** Scrim opacity for the tour backdrop — dims the app while keeping it legible as context. */
private const val TOUR_SCRIM_ALPHA = 0.72f

/**
 * Marks this composable as a tour target under [key]. Records its bounds (in root coordinates) so
 * the overlay can cut a spotlight around it. Safe to leave attached permanently — it only writes a
 * rect on layout.
 */
fun Modifier.tourAnchor(state: TourState, key: String): Modifier =
    onGloballyPositioned { coordinates -> state.anchors[key] = coordinates.boundsInRoot() }

/**
 * [tourAnchor] for targets inside a scrolling container: when the tour reaches [key], the target is
 * scrolled into view first, so the spotlight never points at something off-screen. The container
 * must already have COMPOSED the target (a lazy list only composes what is on or near screen —
 * scroll the list to the target's item before starting the tour).
 */
@OptIn(androidx.compose.foundation.ExperimentalFoundationApi::class)
fun Modifier.tourAnchorInView(state: TourState, key: String): Modifier = composed {
    val requester = remember { BringIntoViewRequester() }
    androidx.compose.runtime.LaunchedEffect(state.visible, state.currentKey) {
        if (state.visible && state.currentKey == key) requester.bringIntoView()
    }
    bringIntoViewRequester(requester).tourAnchor(state, key)
}

/**
 * One tour step: the [key] of a registered [tourAnchor] plus its localized card copy. Steps whose
 * anchor never registered (feature-flagged tab hidden, wide layout without a FAB) are skipped
 * automatically.
 */
data class TourStep(
    val key: String,
    val title: String,
    val body: String,
)

/**
 * Spotlight walkthrough overlay: dims everything behind a scrim, cuts a rounded spotlight around
 * the current step's anchor, and shows a card with the step copy + Skip/Next controls. Tapping
 * anywhere advances. Place as the last child of a Box that contains all anchors so the cutout
 * coordinates line up. Callers pass localized labels (DS components carry no copy).
 */
@Composable
fun FeatureTourOverlay(
    state: TourState,
    steps: List<TourStep>,
    nextLabel: String,
    doneLabel: String,
    skipLabel: String,
    onFinished: (completed: Boolean) -> Unit,
    modifier: Modifier = Modifier,
) {
    if (!state.visible) return
    // Only steps whose anchors actually laid out (flag-gated tabs may be absent on this install).
    val available = steps.filter { state.anchors.containsKey(it.key) }
    if (available.isEmpty()) {
        state.dismiss()
        onFinished(false)
        return
    }
    val index = state.stepIndex.coerceIn(0, available.lastIndex)
    val step = available[index]
    androidx.compose.runtime.SideEffect { state.currentKey = step.key }
    val isLast = index == available.lastIndex
    fun advance() {
        if (isLast) {
            state.dismiss()
            onFinished(true)
        } else {
            state.stepIndex = index + 1
        }
    }

    // Dim — don't hide — the app behind the walkthrough: unlike the image viewer's solid backdrop,
    // a coach mark needs the surrounding UI readable as context for the highlighted control.
    val scrim = AppTheme.colors.scrim.copy(alpha = TOUR_SCRIM_ALPHA)
    val ring = AppTheme.colors.onScrim
    val density = LocalDensity.current
    val cutoutPaddingPx = with(density) { AppTheme.spacing.xs.toPx() }
    val cutoutRadiusPx = with(density) { AppTheme.sizes.tourCutoutRadius.toPx() }
    val ringWidthPx = with(density) { AppTheme.sizes.strokeSm.toPx() }
    var overlaySize by remember { mutableStateOf(Offset.Zero) }

    Box(
        modifier = modifier
            .fillMaxSize()
            .onGloballyPositioned { coordinates ->
                state.hostOrigin = coordinates.boundsInRoot().topLeft
                overlaySize = Offset(coordinates.size.width.toFloat(), coordinates.size.height.toFloat())
            }
            // The overlay swallows every tap: outside taps advance; the highlighted control is for
            // SHOWING, not pressing, during the tour (prevents accidental navigation mid-walkthrough).
            .pointerInput(index) { detectTapGestures { advance() } },
    ) {
        // Anchor bounds are recorded in root coordinates — translate into this overlay's space.
        val target = state.anchors.getValue(step.key).translate(-state.hostOrigin)
        val spotlight = Rect(
            left = target.left - cutoutPaddingPx,
            top = target.top - cutoutPaddingPx,
            right = target.right + cutoutPaddingPx,
            bottom = target.bottom + cutoutPaddingPx,
        )

        Canvas(modifier = Modifier.fillMaxSize()) {
            // Scrim with a rounded-rect hole: path difference renders identically on Android + Skiko.
            val hole = Path().apply {
                addRoundRect(RoundRect(spotlight, CornerRadius(cutoutRadiusPx, cutoutRadiusPx)))
            }
            val full = Path().apply { addRect(Rect(Offset.Zero, size)) }
            drawPath(Path.combine(PathOperation.Difference, full, hole), color = scrim)
            drawRoundRect(
                color = ring,
                topLeft = spotlight.topLeft,
                size = spotlight.size,
                cornerRadius = CornerRadius(cutoutRadiusPx, cutoutRadiusPx),
                style = Stroke(width = ringWidthPx),
            )
        }

        // Card above or below the spotlight — whichever half has more room. With TopCenter the
        // card's natural top is ~0, so offsetting by spotlight.bottom+gap seats it under the target;
        // with BottomCenter its natural bottom is ~overlay height, so a negative offset up to
        // spotlight.top-gap seats it above.
        val below = spotlight.center.y < overlaySize.y / 2f
        val gapPx = with(density) { AppTheme.spacing.md.toPx() }
        Box(
            modifier = Modifier
                .fillMaxSize()
                .padding(AppTheme.spacing.lg),
            contentAlignment = if (below) Alignment.TopCenter else Alignment.BottomCenter,
        ) {
            Column(
                modifier = Modifier
                    .offset {
                        if (below) IntOffset(0, (spotlight.bottom + gapPx).roundToInt())
                        else IntOffset(0, (spotlight.top - gapPx - overlaySize.y).roundToInt())
                    }
                    .widthIn(max = AppTheme.sizes.tourCardMaxWidth)
                    .fillMaxWidth()
                    .background(AppTheme.colors.surface, AppTheme.shapes.card)
                    .padding(AppTheme.spacing.lg),
                verticalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
            ) {
                Text(
                    text = step.title,
                    style = AppTheme.typography.headlineMd,
                    color = AppTheme.colors.onSurface,
                )
                Text(
                    text = step.body,
                    style = AppTheme.typography.bodyMd,
                    color = AppTheme.colors.onSurfaceVariant,
                )
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(AppTheme.spacing.sm),
                ) {
                    AppButton(
                        text = skipLabel,
                        onClick = {
                            state.dismiss()
                            onFinished(false)
                        },
                        variant = AppButtonVariant.Text,
                    )
                    Text(
                        text = "${index + 1}/${available.size}",
                        style = AppTheme.typography.labelMd,
                        color = AppTheme.colors.onSurfaceVariant,
                        modifier = Modifier.weight(1f),
                        textAlign = androidx.compose.ui.text.style.TextAlign.Center,
                    )
                    AppButton(
                        text = if (isLast) doneLabel else nextLabel,
                        onClick = ::advance,
                    )
                }
            }
        }
    }
}

